import { Component, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { LoadingService } from '../../../../services/loading.service';
import { MatToolbarModule } from '@angular/material/toolbar';

type HeaderVariant =
  | 'nola-driver'
  | 'baltimore-driver'
  | 'stg-driver'
  | 'nola-dashboard'
  | 'stg-dashboard'
  | 'storefront';

type City = 'nola' | 'balt' | 'stg';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, MatIconModule, MatToolbarModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  #authService = inject(AuthService);
  #loadingService = inject(LoadingService);
  #router = inject(Router);

  headerVariant = signal<HeaderVariant>('storefront');
  currentCity = signal<City>('nola');

  isLoggedIn = toSignal(this.#authService.isLoggedIn$, { initialValue: false });
  isMenuOpen = signal(false);
  isCityMenuOpen = signal(false);

  ngOnInit(): void {
    const savedCity = localStorage.getItem('oss-city') as City | null;
    if (savedCity === 'nola' || savedCity === 'balt' || savedCity === 'stg') {
      this.currentCity.set(savedCity);
    }

    this.#router.events.pipe(filter((event) => event.type === 1)).subscribe((event: any) => {
      const url = event.urlAfterRedirects;

      if (url.startsWith('/home/balt-driver')) {
        this.currentCity.set('balt');
      } else if (url.startsWith('/home/nola-driver')) {
        this.currentCity.set('nola');
      } else if (url.startsWith('/home/stg-driver')) {
        this.currentCity.set('stg');
      }

      localStorage.setItem('oss-city', this.currentCity());
      this.isCityMenuOpen.set(false);

      switch (true) {
        case url.startsWith('/home'):
        case url.startsWith('/incident-request'):
        case url.startsWith('/auth'):
        case url.startsWith('/admin'): {
          this.headerVariant.set('storefront');
          break;
        }
        case url.startsWith('/nopd'): {
          this.headerVariant.set('nola-dashboard');
          break;
        }
        case url.startsWith('/sgpd'): {
          this.headerVariant.set('stg-dashboard');
          break;
        }
        default: {
          this.headerVariant.set('storefront');
        }
      }
    });
  }

  toggleMenu(): void {
    this.isMenuOpen.update((open) => {
      document.body.style.overflow = open ? '' : 'hidden';
      return !open;
    });
  }

  toggleCityMenu(): void {
    this.isCityMenuOpen.update((open) => !open);
  }

  closeMenu(): void {
    document.body.style.overflow = '';
    this.isMenuOpen.set(false);
    this.isCityMenuOpen.set(false);
  }

  async logoutAsync(): Promise<void> {
    try {
      await this.#loadingService.show('Logging out...');
      await this.#authService.logoutAsync();
      this.closeMenu();
      this.#router.navigateByUrl('/home/nola-driver');
    } catch (error) {
      console.error('error logging out', error);
    } finally {
      this.#loadingService.hide();
    }
  }
}
